import {
  G1N5,
  KVAnnotation,
  KVCameraView,
  KVCameraViewJSON,
  KVMeasurement,
  KVMeasurementJSON,
  KVPlaneIntersection,
  KVPlaneIntersectionJSON,
  KVPolygon,
  KVSurfaceArea,
  KVSurfaceAreaJSON,
  RolodexEntryJson,
} from 'types'
import { GeckoEntryBase } from 'types/entries/geckoEntry'
import { KestrelInspectionType } from 'types/kestrelViewer/inspectionType'
import { parseBoolean, parseString } from 'utils/parsingHelpers'

/**
 * An entry for various photogrammetry representations of a component.
 *
 * A single model should represent a collection of textures / 3D models / links
 *  that were produced from the same set of images (one photogrammetry 'inspection').
 */
export class GeckoKestrelModel extends GeckoEntryBase<
  {
    kestrel_model_id: string
    polygons: KVPolygon[]
    annotations: KVAnnotation[]
    measurements: KVMeasurement[]
    surfaceAreas: KVSurfaceArea[]
    cameraViews: KVCameraView[]
    planeIntersections: KVPlaneIntersection[]
    measurementScale: number
    photosEnabled: boolean
    g1n5: G1N5 | undefined
    inspection_type?: KestrelInspectionType
  },
  {
    organization: string
    site: string
    unit: string
    component: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    const recordData = json.data as Record<string, unknown>

    const polygons = new Array<KVPolygon>()
    const annotations = new Array<KVAnnotation>()
    const measurements = new Array<KVMeasurement>()
    const surfaceAreas = new Array<KVSurfaceArea>()
    const cameraViews = new Array<KVCameraView>()
    const planeIntersections = new Array<KVPlaneIntersection>()
    const measurementScale = recordData?.measurementScale as number | 1
    const photosEnabled = parseBoolean(recordData?.photosEnabled) ?? false
    const inspectionType =
      (recordData?.inspection_type as KestrelInspectionType | undefined) ??
      KestrelInspectionType.NAVY
    let g1n5: G1N5

    if (recordData.polygons) {
      const polygonJSONs = recordData.polygons as Record<string, unknown>[]
      for (const polygon of polygonJSONs) {
        polygons.push(KVPolygon.fromJSON(polygon))
      }
    }

    if (recordData.annotations) {
      const annotationJSONs = recordData.annotations as Record<
        string,
        unknown
      >[]
      for (const annotation of annotationJSONs) {
        annotations.push(KVAnnotation.fromJSON(annotation))
      }
    }

    if (recordData.measurements) {
      const measurementJSONs = recordData.measurements as KVMeasurementJSON[]
      for (const measurement of measurementJSONs) {
        measurements.push(KVMeasurement.fromJSON(measurement))
      }
    }

    if (recordData.surfaceAreas) {
      const surfaceAreaJSONs = recordData.surfaceAreas as KVSurfaceAreaJSON[]
      for (const surfaceArea of surfaceAreaJSONs) {
        surfaceAreas.push(KVSurfaceArea.fromJSON(surfaceArea))
      }
    }

    if (recordData.planeIntersections) {
      const planeIntersectionJSONs =
        recordData.planeIntersections as KVPlaneIntersectionJSON[]
      for (const planeIntersection of planeIntersectionJSONs) {
        planeIntersections.push(KVPlaneIntersection.fromJSON(planeIntersection))
      }
    }

    if (recordData.g1n5) {
      g1n5 = G1N5.fromJSON(recordData.g1n5)
    } else {
      g1n5 = new G1N5()
    }

    if (recordData.cameraViews) {
      const cameraViewJSONs = recordData.cameraViews as KVCameraViewJSON[]
      for (const cameraView of cameraViewJSONs) {
        cameraViews.push(KVCameraView.fromJSON(cameraView))
      }
    }

    super(
      json,
      {
        kestrel_model_id: (recordData.kestrel_model_id ?? '') as string,
        polygons: polygons,
        annotations: annotations,
        measurements: measurements,
        surfaceAreas: surfaceAreas,
        cameraViews: cameraViews,
        planeIntersections: planeIntersections,
        measurementScale: measurementScale,
        photosEnabled: photosEnabled,
        g1n5: g1n5,
        inspection_type: inspectionType,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoKestrelModel(json)
  }
}
